import {useEffect, useRef} from 'react';
import equal from 'fast-deep-equal/react';

type UseWhenChangedEffectOptions = {
	executeAtMount?: boolean;
	onlyOnUpdate?: boolean;
};

// TODO: the more I utilize this the more i think it's a bad idea

/**
 * Should only be used when we absoluteluy need to respond to actual changes
 * in a value. This is not a good idea for most cases, but it can be useful
 * in some cases.
 * @param thingThatMightChange
 * @param callback
 * @param options
 * @example
 * ```tsx
 * const [complexValue, setComplexValue] = useState({a: 1, b: 2});
 * useWhenChangedEffect(complexValue, (newValue, oldValue) => {
 *   console.log('Value changed from', oldValue, 'to', newValue);
 * });
 * ```
 */
export const useWhenChangedEffect = <T>(
	thingThatMightChange: T,
	callback?: (thingThatMightChange: T, oldValue?: T) => void | (() => void),
	options?: UseWhenChangedEffectOptions,
) => {
	const thingThatMightChangeRef = useRef(thingThatMightChange);
	const ranOnceRef = useRef(false);
	const callbackRef = useRef(callback);
	const mounted = useRef(false);
	const clenupFn = useRef<Function>();

	useEffect(() => {
		callbackRef.current = callback;
	});

	useEffect(() => {
		if (equal(thingThatMightChangeRef.current, thingThatMightChange)) {
			if (!options?.executeAtMount || mounted.current) {
				mounted.current = true;
				return;
			}
		}

		const oldValue = thingThatMightChangeRef.current;
		thingThatMightChangeRef.current = thingThatMightChange;
		mounted.current = true;

		if (!ranOnceRef.current) {
			ranOnceRef.current = true;
			if (options?.onlyOnUpdate) return;
		}

		clenupFn.current?.();

		const result = callbackRef.current?.(thingThatMightChange, oldValue);
		clenupFn.current = typeof result === 'function' ? result : undefined;
	}, [thingThatMightChange, options?.onlyOnUpdate, options?.executeAtMount]);

	useEffect(() => {
		return () => {
			clenupFn.current?.();
		};
	}, []);
};

export default useWhenChangedEffect;
